/** Result records, their transport, and what the device can say about itself. */
import type { CaseSpec, RenderMode } from "./plan.js";

export interface SessionFacts {
  presenting: boolean;
  isMultiview: boolean | null;
  layersGranted: boolean | null;
  features: string[];
  /** Cameras in the XR array camera at measurement time (2 in stereo). */
  views: number | null;
  /** XRSession.frameRate at measurement time (72 or 90 on a Quest). */
  frameRate: number | null;
}

export interface GpuTiming {
  avgMs: number;
  p95Ms: number;
  maxMs: number;
  samples: number;
}

export interface SnapshotStats {
  /** Fraction of the drawing buffer that differs from the clear colour. */
  coverage: number;
  /** Mean brightness (0–255) of those pixels: 0 means the model rendered black. */
  meanLuma: number;
}

/**
 * What went wrong while a case was on screen. Shader failures are the
 * automated evidence for "nothing rendered" in VR, where no snapshot exists:
 * three.js still counts the triangles of a program that failed to link.
 */
export interface CaseErrors {
  /** Programs three.js failed to link (name = material type) with the shader logs, new since the case started. */
  shader: { program: string; log: string }[];
  /** WebGL error names sampled after renders during the case. */
  gl: string[];
  /** console.error / console.warn lines during the case (three.js reports shader failures here too). */
  console: string[];
}

export interface CaseResult {
  id: string;
  title: string;
  /** 2D pass only. */
  snapshot?: SnapshotStats;
  spec: Omit<CaseSpec, "title" | "watch">;
  render: RenderMode;
  /** Whether three.js was allowed to use WEBGL_multi_draw for this case. */
  multiDraw: boolean;
  errors: CaseErrors;
  frames: number;
  /** CPU frame pacing: the interval between frames. A GPU-bound headset keeps this at the display rate and drops frames instead. */
  avgMs: number;
  p50Ms: number;
  p95Ms: number;
  maxMs: number;
  fps: number;
  /** GPU time per render() from EXT_disjoint_timer_query_webgl2, when the browser exposes it. */
  gpu?: GpuTiming;
  /** Draw calls and triangles of the last render (after culling: gaze-dependent). */
  calls: number;
  triangles: number;
  /** Triangles resident in the batches for this case (the level's total for the visible systems). */
  loadedTriangles: number;
  culled: number;
  /**
   * Sub-draws submitted for the frame. `calls` counts a whole multi-draw as one,
   * so it never showed the ~2,200 sub-draws session 2 measured as the cost.
   */
  runs: number;
  instances: number;
  session: SessionFacts;
  startedAt: string;
  /** Set when the pass was cut short (the tester entered VR during the 2D pass). */
  interrupted?: boolean;
}

export interface PassResult {
  mode: "2d" | "xr";
  startedAt: string;
  finishedAt: string;
  cases: CaseResult[];
}

export interface DeviceFacts {
  userAgent: string;
  looksLikeQuest: boolean;
  webgl2: boolean;
  multiDraw: boolean;
  multiviewExtension: boolean;
  /** EXT_disjoint_timer_query_webgl2 available: GPU frame times can be measured. */
  gpuTimer: boolean;
  /** The build's super-three multiview shader-prefix fix: "applied", "upstream", "not-multiview", or null when the build carries no stamp. */
  multiviewFix: string | null;
  threeRevision: string;
  screen: [number, number];
  devicePixelRatio: number;
}

export interface Observations {
  /** caseId → what the tester saw. */
  flicker: Record<string, "no" | "yes" | "unsure">;
  comfort: "fine" | "judder" | "unusable" | "";
  notes: string;
  tester: string;
}

export interface ResultRecord {
  id: string;
  createdAt: string;
  bundle: { id: string; run: string; base: string };
  plan: string;
  device: DeviceFacts;
  passes: PassResult[];
  /** caseId → JPEG data URL, captured in 2D only. */
  snapshots: Record<string, string>;
  log: string[];
  observations: Observations | null;
  submitted: { posted: boolean; stored: boolean; at: string | null; error: string | null };
}

export function newId(): string {
  const c = globalThis.crypto as Crypto | undefined;
  return c?.randomUUID ? c.randomUUID() : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

export function percentile(values: number[], p: number): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  return sorted[Math.min(sorted.length - 1, Math.floor(p * sorted.length))]!;
}

export function summarise(frames: number[]): { avgMs: number; p50Ms: number; p95Ms: number; maxMs: number; fps: number } {
  const avg = frames.length ? frames.reduce((a, b) => a + b, 0) / frames.length : 0;
  return { avgMs: avg, p50Ms: percentile(frames, 0.5), p95Ms: percentile(frames, 0.95), maxMs: frames.length ? Math.max(...frames) : 0, fps: avg > 0 ? 1000 / avg : 0 };
}

const STORAGE_KEY = "atlas-harness:results";

export function saveLocal(record: ResultRecord): void {
  try {
    const all = loadLocal();
    const i = all.findIndex((r) => r.id === record.id);
    if (i >= 0) all[i] = record;
    else all.push(record);
    while (all.length > 5) all.shift();
    localStorage.setItem(STORAGE_KEY, JSON.stringify(all));
  } catch {
    /* storage may be unavailable; the POST is the real path */
  }
}

export function loadLocal(): ResultRecord[] {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) ?? "[]") as ResultRecord[];
  } catch {
    return [];
  }
}

/** POST the record to the Pages Function; resolves with what the server said. */
export async function submit(record: ResultRecord, endpoint = "api/results"): Promise<ResultRecord["submitted"]> {
  const at = new Date().toISOString();
  try {
    const response = await fetch(endpoint, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(record) });
    if (!response.ok) return { posted: false, stored: false, at, error: `HTTP ${response.status}` };
    const body = (await response.json()) as { stored?: boolean; reason?: string };
    return { posted: true, stored: body.stored === true, at, error: body.stored ? null : (body.reason ?? "not stored") };
  } catch (error) {
    return { posted: false, stored: false, at, error: error instanceof Error ? error.message : String(error) };
  }
}
